import {gql} from "@apollo/client";
import {mutate, query} from "../../lib/apollo";

export type BillablesMonth = { year: number; month: number };

export type BillablesMonthStat = {
    year: number;
    month: number;
    good: number;
    billable: number;
    goodRev: number;
    billableRev: number;
    missing?: number | null;
};

export type BillablesBrokerRow = {
    company: string;
    companyId: number;
    broker: string;
    brokerId: number;
    months: BillablesMonthStat[];
};

export type BillablesSchoolGroupRow = {
    school: string;
    schoolGroupId: number;
    hpCampaignId: number;
    months: BillablesMonthStat[];
};

export type BillablesAcceptedMonth = {
    year: number;
    month: number;
    good: number;
    goodRev: number;
    billable: number | null;
    billableRev: number | null;
    paid: boolean;
};

export type BillablesAcceptedDetail = {
    company: string;
    companyId: number;
    broker: string;
    brokerId: number;
    school: string;
    schoolGroupId: number;
    hpCampaignId: number;
    months: BillablesAcceptedMonth[];
    canEditPaid: boolean;
};

export async function getBillablesBrokers(startDate: string, endDate: string, companyIds?: number[]) {
    const q = gql`
        query q($input: BillablesRangeInput!) {
            billablesBrokers(input: $input) {
                months { year month }
                brokers {
                    company
                    companyId
                    broker
                    brokerId
                    months {
                        year month good billable goodRev billableRev missing
                    }
                }
            }
        }
    `;
    const response = await query(q, {
        input: {startDate, endDate, companyIds: companyIds?.length ? companyIds : null},
    });
    return response?.billablesBrokers as {months: BillablesMonth[]; brokers: BillablesBrokerRow[]} | null;
}

export async function getBillablesSchoolGroups(
    startDate: string,
    endDate: string,
    brokerId: number,
    companyId: number
) {
    const q = gql`
        query q($input: BillablesSchoolGroupsInput!) {
            billablesSchoolGroups(input: $input) {
                school
                schoolGroupId
                hpCampaignId
                months {
                    year month good billable goodRev billableRev
                }
            }
        }
    `;
    const response = await query(q, {
        input: {startDate, endDate, brokerId, companyId},
    });
    return (response?.billablesSchoolGroups || []) as BillablesSchoolGroupRow[];
}

export async function getBillablesAcceptedDetail(hpCampaignId: number) {
    const q = gql`
        query q($input: BillablesAcceptedDetailInput!) {
            billablesAcceptedDetail(input: $input) {
                company companyId broker brokerId school schoolGroupId hpCampaignId canEditPaid
                months {
                    year month good goodRev billable billableRev paid
                }
            }
        }
    `;
    const response = await query(q, {input: {hpCampaignId}});
    return response?.billablesAcceptedDetail as BillablesAcceptedDetail | null;
}

export async function billablesAdjust(input: {
    schoolGroupId: number;
    companyId: number;
    brokerId: number;
    year: number;
    month: number;
    expectedBilled: number;
    newAmount: number;
}) {
    const m = gql`
        mutation m($input: BillablesAdjustInput!) {
            billablesAdjust(input: $input) {
                addition leads oldBilled newBilled
            }
        }
    `;
    const response = await mutate(m, {input});
    return response?.billablesAdjust as {
        addition: number;
        leads: number;
        oldBilled: number;
        newBilled: number;
    } | null;
}

export async function billablesAcceptedUpsert(input: {
    companyId: number;
    brokerId: number;
    schoolGroupId: number;
    months: {year: number; month: number; billableCount: number | null}[];
    paidMonths: {year: number; month: number}[];
}) {
    const m = gql`
        mutation m($input: BillablesAcceptedUpsertInput!) {
            billablesAcceptedUpsert(input: $input)
        }
    `;
    const response = await mutate(m, {input});
    return !!response?.billablesAcceptedUpsert;
}

export function monthKey(month: number, year: number) {
    return `${month}_${year}`;
}

export function findMonthStat(months: BillablesMonthStat[] | undefined, m: number, y: number) {
    return (months || []).find((s) => s.month === m && s.year === y) || null;
}

export function formatMoney(n: number | null | undefined) {
    if (n === null || n === undefined || Number.isNaN(n)) {
        return "";
    }
    return n.toLocaleString("en-US", {style: "currency", currency: "USD"});
}

export function formatCount(n: number | null | undefined) {
    if (n === null || n === undefined || Number.isNaN(n)) {
        return "";
    }
    return n.toLocaleString("en-US");
}

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function monthLabel(month: number) {
    return MONTH_LABELS[month - 1] || String(month);
}

export function monthFullLabel(month: number) {
    return (
        [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
        ][month - 1] || String(month)
    );
}

/** Last 3 completed months (exclude current). Front month = newest of that window. */
export function defaultBillablesWindow(now = new Date()) {
    const front = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return {year: front.getFullYear(), frontMonth: front.getMonth() + 1};
}

/** Front month + 2 prior months (chronological). Year is the front month's year. */
export function windowMonths(year: number, frontMonth: number): BillablesMonth[] {
    const months: BillablesMonth[] = [];
    let y = year;
    let m = frontMonth;
    for (let i = 0; i < 3; i++) {
        months.unshift({year: y, month: m});
        m -= 1;
        if (m < 1) {
            m = 12;
            y -= 1;
        }
    }
    return months;
}

export function windowDateRange(year: number, frontMonth: number) {
    const months = windowMonths(year, frontMonth);
    const first = months[0];
    const last = months[months.length - 1];
    const pad = (n: number) => String(n).padStart(2, "0");
    return {
        startDate: `${first.year}-${pad(first.month)}-01`,
        endDate: `${last.year}-${pad(last.month)}-01`,
        monthsChronological: months,
        monthsDisplay: [...months].reverse(),
        label: `${monthLabel(first.month)}–${monthLabel(last.month)} ${last.year === first.year ? first.year : `${first.year}/${last.year}`}`,
    };
}
